import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { register, login, getGoogleLoginUrl, buildShopifyAdminAppUrl } from '../api/auth';
import { getPublicPlans } from '../api/subscription';
import { prefetchSignupPlatforms } from '../api/platforms';
import { markFreshSignup } from '../utils/signupGate';
import aerochatLogo from '../assets/aerochat_logo_full.svg';
import '../styles/pages/login.css';

const SignupPage = ({ onLoginSuccess }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const [searchParams] = useSearchParams();
  const planId = searchParams.get('plan') || 'trial';
  const cycle = searchParams.get('cycle') || 'monthly';
  // Partner/referral attribution: prefer the URL param, fall back to the
  // aerochat_ref cookie set by the referral redirect (GET /bot/api/ref/{code}).
  const readCookie = (name) => {
    const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  };
  const referralCode = searchParams.get('ref') || readCookie('aerochat_ref');
  const promoCode = searchParams.get('promo') || searchParams.get('promo_code');
  const isPaidPlan = planId !== 'free' && planId !== 'trial';
  const showTrialBanner = planId !== 'free';

  const [planLabel, setPlanLabel] = useState(planId);
  const [trialDays, setTrialDays] = useState(7);

  useEffect(() => {
    getPublicPlans()
      .then(({ plans, trial_duration_days }) => {
        if (isPaidPlan) {
          const match = plans.find((p) => p.id === planId);
          if (match) setPlanLabel(match.name);
        }
        if (trial_duration_days) setTrialDays(trial_duration_days);
      })
      .catch(() => {});
    // Warm the platforms cache so /platform-select renders instantly after submit
    prefetchSignupPlatforms();
  }, [planId, isPaidPlan]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Shopify store URL view
  const [view, setView] = useState('main'); // 'main' | 'shopify'
  const [storeUrl, setStoreUrl] = useState('');
  const [shopifyError, setShopifyError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim() || !password.trim()) {
      setError(t('signup.validation.fillRequired'));
      return;
    }
    if (password.length < 6) {
      setError(t('signup.validation.passwordLength'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Extract name from email for registration
      const namePart = email.split('@')[0];
      await register({
        firstName: namePart,
        lastName: '',
        email: email.trim(),
        password,
        planId,
        planType: cycle,
        ref: referralCode,
        promoCode,
      });

      // Auto-login after successful registration
      const data = await login(email.trim(), password);
      onLoginSuccess(data.user);
      // Brand-new signup — flag it so the mobile desktop-gate engages on phones
      // (no-op on desktop). Cleared on logout / once onboarding starts.
      markFreshSignup();
      navigate('/platform-select', { replace: true });
    } catch (err) {
      setError(err.message || t('signup.validation.registrationFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    window.location.href = getGoogleLoginUrl({ plan: planId, cycle });
  };

  const handleShopifySubmit = (e) => {
    e.preventDefault();
    setShopifyError('');

    const url = storeUrl.trim().toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/\/+$/, '');

    if (!url.includes('.myshopify.com')) {
      setShopifyError(t('shopify.validation.invalidStoreUrl'));
      return;
    }

    const storeName = url.split('.myshopify.com')[0];
    if (!storeName) {
      setShopifyError(t('shopify.validation.invalidStoreUrl'));
      return;
    }

    // Hands the merchant to their Shopify admin, where opening the embedded
    // app IS the session — there is no OAuth round trip to run any more.
    const adminUrl = buildShopifyAdminAppUrl(storeName);
    if (!adminUrl) {
      setShopifyError(t('shopify.validation.appUnavailable'));
      return;
    }
    window.location.href = adminUrl;
  };

  // ─── Shopify Store URL View ───
  if (view === 'shopify') {
    return (
      <div className="login-page">
        <div className="login-card">
          <div className="login-card__header">
            <h1 className="login-card__title">{t('shopify.title')}</h1>
            <p className="login-card__subtitle">{t('shopify.subtitle')}</p>
          </div>

          <form className="login-card__form" onSubmit={handleShopifySubmit}>
            <div className="login-card__field">
              <label className="login-card__label">{t('shopify.storeUrlLabel')}</label>
              <input
                type="text"
                className="login-card__input"
                placeholder={t('shopify.storeUrlPlaceholder')}
                value={storeUrl}
                onChange={(e) => { setStoreUrl(e.target.value); setShopifyError(''); }}
                autoFocus
              />
              {shopifyError && (
                <span className="login-card__field-error">{shopifyError}</span>
              )}
              <span className="login-card__field-hint">{t('shopify.storeUrlHint')}</span>
            </div>

            <button type="submit" className="login-card__submit">
              {t('shopify.continue')}
            </button>
          </form>

          <button
            type="button"
            className="login-card__back-btn"
            onClick={() => { setView('main'); setStoreUrl(''); setShopifyError(''); }}
          >
            {t('shopify.backToSignIn')}
          </button>
        </div>
      </div>
    );
  }

  // ─── Main Signup View ───
  return (
    <div className="login-page">
      <div className="login-card">
        {/* Logo */}
        <div className="login-card__logo">
          <img src={aerochatLogo} alt="AeroChat" className="login-card__logo-img" />
        </div>

        <div className="login-card__header">
          <h1 className="login-card__title">{t('signup.title')}</h1>
          <p className="login-card__subtitle">{t('signup.subtitle', { days: trialDays })}</p>
        </div>

        {showTrialBanner && (
          <div className="login-card__plan-banner">
            <span className="login-card__plan-badge">{isPaidPlan ? planLabel : 'TRIAL'}</span>
            <span className="login-card__plan-trial-text">
              {t('signup.planTrialMessage', { plan: isPaidPlan ? planLabel : t('signup.trialPlanName', 'trial'), days: trialDays })}
            </span>
          </div>
        )}

        {/* Shopify Merchant Card */}
        <div className="signup-shopify-card">
          <h3 className="signup-shopify-card__title">{t('signup.shopifyMerchantTitle')}</h3>
          <p className="signup-shopify-card__desc">{t('signup.shopifyMerchantDesc')}</p>
          <button
            type="button"
            className="signup-shopify-card__btn"
            onClick={() => setView('shopify')}
          >
            {t('signup.installFromShopify')}
          </button>
        </div>

        {/* Divider */}
        <div className="login-card__divider">
          <span className="login-card__divider-line" />
          <span className="login-card__divider-text">{t('signup.notOnShopify')}</span>
          <span className="login-card__divider-line" />
        </div>

        {/* Google Sign Up */}
        <div className="login-card__social">
          <button type="button" className="login-card__social-btn" onClick={handleGoogleSignup}>
            <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
              <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
              <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
              <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
            </svg>
            {t('login.continueWithGoogle')}
          </button>
        </div>

        {/* Divider */}
        <div className="login-card__divider">
          <span className="login-card__divider-line" />
          <span className="login-card__divider-text">{t('signup.orSignUpWithEmail')}</span>
          <span className="login-card__divider-line" />
        </div>

        {/* Email/Password Form */}
        <form className="login-card__form" onSubmit={handleSubmit}>
          {error && (
            <div className="login-card__error">{error}</div>
          )}

          <div className="login-card__field">
            <label className="login-card__label">{t('signup.emailLabel')}</label>
            <input
              type="email"
              className="login-card__input"
              placeholder={t('signup.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
            />
          </div>

          <div className="login-card__field">
            <label className="login-card__label">{t('signup.passwordLabel')}</label>
            <div className="login-card__input-wrap">
              <input
                type={showPassword ? 'text' : 'password'}
                className="login-card__input login-card__input--password"
                placeholder={t('signup.passwordPlaceholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button type="button" className="login-card__eye-btn" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                {showPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="login-card__submit login-card__submit--green"
            disabled={loading}
          >
            {loading
              ? t('signup.startingTrial')
              : isPaidPlan
                ? t('signup.startPlanTrial', { plan: planLabel })
                : t('signup.startFreeTrial')
            }
          </button>
        </form>

        {/* Footer */}
        <p className="login-card__footer">
          {t('signup.alreadyHaveAccount')}{' '}
          <button
            type="button"
            className="login-card__link"
            onClick={() => navigate('/login')}
            style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: 'pointer' }}
          >
            {t('signup.signIn')}
          </button>
        </p>

        <p className="login-card__footer" style={{ marginTop: 8 }}>
          {t('login.termsText')}{' '}
          <a href="https://aerochat.ai/legal/terms" target="_blank" rel="noopener noreferrer" className="login-card__link">{t('login.termsLink')}</a> {t('login.and')}{' '}
          <a href="https://aerochat.ai/legal/privacy" target="_blank" rel="noopener noreferrer" className="login-card__link">{t('login.privacyLink')}</a>
        </p>
      </div>
    </div>
  );
};

export default SignupPage;
